/**
 * La pantalla del Pacto — ADR-028.
 *
 * SIN CHECKBOX, y es una decisión, no un olvido. El texto completo está
 * arriba del botón: la persona lo vio por construcción. Un checkbox cuesta
 * conversión y compra cero — lo que buscamos no es un contrato ejecutable,
 * es que lo haya leído.
 *
 * El botón nombra el acto ("Acepto. Entro.") para que aceptar no pueda ser
 * un accidente. Y el rechazo NO es un callejón: "Mejor no, por ahora" deja
 * el pase vivo hasta su fecha. Una puerta que castiga por leer y pensarlo
 * deslegitima el pacto entero.
 */

import { useEffect, useState } from 'react';

import { Boton, Pantalla, Stack, Texto, Titulo } from '../../shared/ui/primitives.js';
import * as api from '../../shared/lib/api.js';

export interface PactScreenProps {
  /** Qué puerta: cambia el encabezado, no el texto del Pacto. */
  puerta: 'invitacion' | 'solicitud' | 'reaceptacion';
  onAceptar: () => void;
  onRechazar?: () => void;
  /** Si hay sesión, aceptar se registra en el servidor antes de continuar. */
  registrarEnServidor?: boolean;
}

const ENCABEZADO: Record<PactScreenProps['puerta'], { titulo: string; bajada: string }> = {
  invitacion: {
    titulo: 'Antes de gastar el pase',
    bajada: 'Esto es todo lo que te pedimos. No cambia.',
  },
  solicitud: {
    titulo: 'Cómo nos tratamos aquí',
    bajada: 'Si esto te suena bien, mándanos tu solicitud.',
  },
  reaceptacion: {
    titulo: 'Cambiamos una línea del Pacto',
    bajada: 'Léelo otra vez y seguimos donde estabas.',
  },
};

export function PactScreen({
  puerta,
  onAceptar,
  onRechazar,
  registrarEnServidor = false,
}: PactScreenProps) {
  const [pacto, setPacto] = useState<api.Pact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    void api
      .fetchPact()
      .then((p) => {
        if (vivo) setPacto(p);
      })
      .catch(() => {
        if (vivo) setError('No pudimos traer el Pacto. Revisa tu conexión.');
      });
    return () => {
      vivo = false;
    };
  }, []);

  async function aceptar() {
    if (!pacto || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      if (registrarEnServidor) await api.acceptPact(pacto.version);
      onAceptar();
    } catch {
      setError('No pudimos guardar tu aceptación. Vuelve a intentar.');
      setEnviando(false);
    }
  }

  const { titulo, bajada } = ENCABEZADO[puerta];

  return (
    <Pantalla>
      <Stack>
        <header>
          <Titulo>{titulo}</Titulo>
          <Texto variante="secundario">{bajada}</Texto>
        </header>

        {error && <Texto variante="secundario">{error}</Texto>}

        {pacto && (
          <div
            style={{
              background: 'var(--gu-surface-raised)',
              borderRadius: 'var(--gu-radius-lg)',
              padding: 'var(--gu-space-4)',
              display: 'grid',
              gap: 'var(--gu-space-4)',
            }}
          >
            {pacto.bloques.map((b) => (
              <div key={b.titulo}>
                <Texto>
                  <strong>{b.titulo}</strong> {b.texto}
                </Texto>
              </div>
            ))}
          </div>
        )}

        <Boton onClick={() => void aceptar()} disabled={!pacto || enviando}>
          Acepto. Entro.
        </Boton>

        {onRechazar && (
          <Boton variante="secundario" onClick={onRechazar}>
            Mejor no, por ahora
          </Boton>
        )}
      </Stack>
    </Pantalla>
  );
}
